import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { SkeletonList } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Dialog } from '@/shared/ui/Dialog';
import { TransferCard } from '@/shared/ui/TransferCard';
import { useToast } from '@/shared/ui/Toast';
import { closeSettlement, completeTransfer, getSettlement } from '@/shared/api/settlement';
import { readyKakaoPayment } from '@/shared/api/payment';
import { listMeetingPayoutAccounts, type PayoutAccount } from '@/shared/api/payoutAccount';
import { listParticipants, getMeeting } from '@/shared/api/meetings';
import { useAuthStore } from '@/shared/auth/store';
import { formatKRW } from '@/shared/lib/money';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { CharSpringMorph } from '@/shared/ui/CharSpringMorph';
import { MoneyTabs } from '@/features/meeting/MoneyTabs';

const SETTLEMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: '정산 대기',
  CALCULATED: '계산 완료',
  COMPLETED: '정산 완료',
};

// 카카오페이 단건결제로 정산금을 수납하는 구조는 전금법상 PG업 등록이 필요해
// 프로덕션 노출 금지 — dev 테스트 전용 플래그 (2026-06 카카오페이 1차 심사 반려)
const KAKAOPAY_DEV_ENABLED = import.meta.env.VITE_KAKAOPAY_ENABLED === 'true';

export default function SettlementPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState<{ id: string; toComplete: boolean } | null>(null);

  const meetingQ = useQuery({ queryKey: ['meeting', id], queryFn: () => getMeeting(id) });
  const partsQ = useQuery({
    queryKey: ['participants', id],
    queryFn: () => listParticipants(id),
    enabled: !!id,
  });
  const settleQ = useQuery({
    queryKey: ['settlement', id],
    queryFn: () => getSettlement(id),
    enabled: !!id,
  });
  const payoutAccountsQ = useQuery({
    queryKey: ['payout-accounts', id],
    queryFn: () => listMeetingPayoutAccounts(id),
    enabled: !!id,
  });

  const completeMut = useMutation({
    mutationFn: (args: { transferId: string; completed: boolean }) =>
      completeTransfer(args.transferId, args.completed),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['settlement', id] });
      toast.success(vars.completed ? '송금 완료로 표시했어요.' : '송금을 재개했어요.');
    },
    onError: () => toast.error('상태 변경에 실패했어요.'),
  });
  const kakaoPayMut = useMutation({
    mutationFn: (transferId: string) => readyKakaoPayment(transferId),
    onSuccess: (res) => {
      // 결제창 복귀(전체 리로드) 후 돌아올 정산 페이지 경로 보존
      sessionStorage.setItem('fp-pay-return', `/meetings/${id}/settlement`);
      window.location.href = res.redirectPcUrl;
    },
    onError: () => toast.error('카카오페이 결제 준비에 실패했어요.'),
  });
  const closeMut = useMutation({
    mutationFn: () => closeSettlement(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settlement', id] });
      qc.invalidateQueries({ queryKey: ['meeting', id] });
      setConfirmingClose(false);
      toast.success('정산을 마감했어요.');
    },
    onError: () => toast.error('정산 마감에 실패했어요.'),
  });

  if (settleQ.isLoading) return <SkeletonList count={3} />;
  if (settleQ.isError || !settleQ.data) {
    return <ErrorState title="정산을 불러오지 못했어요" onRetry={() => settleQ.refetch()} />;
  }
  const s = settleQ.data;
  const isHost = user?.id === meetingQ.data?.hostUserId;
  const transfers = s.transfers ?? [];
  const perUser = s.perUser ?? [];
  const statusOf = (transfer: (typeof transfers)[number]) =>
    transfer.status ?? ((transfer as unknown as { completed?: boolean }).completed ? 'COMPLETED' : 'PENDING');
  const totalTransfers = transfers.length;
  const doneCount = transfers.filter((t) => statusOf(t) === 'COMPLETED').length;
  const allDone = doneCount === totalTransfers;
  const doneRatio = totalTransfers === 0 ? 0 : Math.round((doneCount / totalTransfers) * 100);

  const nicknameOf = (uid: string) =>
    partsQ.data?.find((p) => p.userId === uid)?.nickname ?? uid.slice(-4);
  const myTransfers = user
    ? transfers.filter((t) => t.fromUserId === user.id || t.toUserId === user.id)
    : [];
  const mySendTotal = myTransfers
    .filter((t) => t.fromUserId === user?.id && statusOf(t) !== 'COMPLETED')
    .reduce((sum, t) => sum + t.amount, 0);
  const myReceiveTotal = myTransfers
    .filter((t) => t.toUserId === user?.id && statusOf(t) !== 'COMPLETED')
    .reduce((sum, t) => sum + t.amount, 0);
  const pendingTransfers = transfers.filter((t) => statusOf(t) !== 'COMPLETED');
  const completedTransfers = transfers.filter((t) => statusOf(t) === 'COMPLETED');
  const canCloseSettlement = meetingQ.data?.status === 'SETTLING';

  // 다이렉트 송금 보조: 받는 사람 계좌·금액을 클립보드로 — 앱은 자금을 중개하지 않는다.
  const copyAccount = async (account: PayoutAccount, amount: number) => {
    const text = `${account.bankName} ${account.accountNumber} (${account.holderName}) ${formatKRW(amount)}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('계좌와 금액을 복사했어요. 송금 앱에 붙여넣어 보내주세요.');
    } catch {
      toast.error('복사에 실패했어요. 계좌번호를 직접 확인해주세요.');
    }
  };

  return (
    <section className="space-y-6">
      <h1 className="sr-only">정산</h1>
      <MeetingTabs meetingId={id} />
      <MoneyTabs meetingId={id} />

      <section className="relative overflow-hidden rounded-[22px] bg-[image:var(--money-gradient-settlement)] px-4 py-4 text-white shadow-[var(--money-glow)]">
        <div className="absolute -left-10 -top-10 h-32 w-32 rounded-full bg-primary-light/18" aria-hidden="true" />
        <div className="relative flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/65">정산 진행</p>
            <CharSpringMorph
              value={formatKRW(s.totalAmount)}
              gap={0}
              animateOnMount
              className="num-display mt-2 text-[31px] leading-none tracking-[-0.8px] text-white"
            />
            <p className="mt-2 text-[12px] font-medium text-white/72">
              {totalTransfers === 0 ? '송금할 내역이 없어요' : `${doneCount}건 완료 · ${totalTransfers - doneCount}건 남음`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3 rounded-[18px] border border-primary-light/20 bg-primary-light/15 p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.16)] backdrop-blur">
            <div
              className="relative h-16 w-16 rounded-full"
              style={{
                background: `conic-gradient(var(--color-primary-light) 0 ${doneRatio}%, rgba(255,255,255,0.16) ${doneRatio}% 100%)`,
              }}
              role="img"
              aria-label={`정산 진행률 ${doneRatio}%`}
            >
              <div className="absolute inset-2.5 rounded-full bg-[var(--money-ring-hole)]" />
              <span className="absolute inset-0 flex items-center justify-center text-[12px] font-semibold text-white">
                {doneRatio}%
              </span>
            </div>
            <div className="text-[10px] font-semibold leading-tight text-white/75">
              {doneCount}/{totalTransfers}
              <br />
              완료
            </div>
          </div>
        </div>
      </section>

      <Card className="!border-surface-border !p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-small font-semibold tracking-[0.04em] text-ink-faint">내 정산</h2>
          <span
            className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
              s.status === 'COMPLETED'
                ? 'bg-success-soft text-success-strong'
                : 'bg-warning-soft text-warning'
            }`}
          >
            {SETTLEMENT_STATUS_LABEL[s.status] ?? s.status}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-error-soft px-3 py-2.5">
            <p className="text-[10px] font-semibold text-error">보낼 돈</p>
            <p className="mt-1 text-[17px] font-semibold leading-none tracking-tight text-ink">
              {formatKRW(mySendTotal)}
            </p>
            <p className="mt-1 text-[10px] text-ink-muted">
              {myTransfers.filter((t) => t.fromUserId === user?.id && statusOf(t) !== 'COMPLETED').length}건 미완료
            </p>
          </div>
          <div className="rounded-xl bg-success-soft px-3 py-2.5">
            <p className="text-[10px] font-semibold text-success-strong">받을 돈</p>
            <p className="mt-1 text-[17px] font-semibold leading-none tracking-tight text-ink">
              {formatKRW(myReceiveTotal)}
            </p>
            <p className="mt-1 text-[10px] text-ink-muted">
              {myTransfers.filter((t) => t.toUserId === user?.id && statusOf(t) !== 'COMPLETED').length}건 대기
            </p>
          </div>
        </div>
      </Card>

      <Card className="!border-surface-border !p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-small font-semibold text-ink">참여자별 정산</h2>
          <span className="text-[11px] font-medium text-ink-muted">{perUser.length}명</span>
        </div>
        <ul className="space-y-1.5">
          {perUser.map((u) => {
            const net = u.net;
            return (
              <li key={u.userId} className="flex items-center justify-between rounded-xl bg-surface-muted px-3 py-2">
                <span className="min-w-0 truncate text-small font-semibold text-ink">{nicknameOf(u.userId)}</span>
                {net === 0 ? (
                  <span className="text-[11px] font-medium text-ink-muted">균등 분배</span>
                ) : (
                  <span className={`text-small font-semibold ${net > 0 ? 'text-success-strong' : 'text-error'}`}>
                    {net > 0 ? '받을 금액' : '낼 금액'} {formatKRW(Math.abs(net))}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      <section aria-labelledby="transfer-heading" className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 id="transfer-heading" className="text-feature font-semibold tracking-tight-feature text-ink">
            정산 결과
          </h2>
          <span className="text-small font-medium text-ink-muted">
            미완료 {pendingTransfers.length}
          </span>
        </div>

        {transfers.length === 0 ? (
          <Card className="!border-surface-border !p-4">
            <p className="text-center text-body text-ink-muted">
              비용이 균등하게 나뉘어 추가 송금이 필요 없어요.
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {pendingTransfers.length > 0 ? (
              <ul className="space-y-2" aria-label="미완료 송금 내역">
                {pendingTransfers.map((t) => {
                  const canComplete =
                    t.fromUserId === user?.id || t.toUserId === user?.id || isHost;
                  const completed = statusOf(t) === 'COMPLETED';
                  const baseProps = {
                    fromName: nicknameOf(t.fromUserId),
                    toName: nicknameOf(t.toUserId),
                    amount: t.amount,
                    completed,
                  };
                  const isPayer = t.fromUserId === user?.id;
                  const payeeAccount = payoutAccountsQ.data?.[t.toUserId];
                  return (
                    <li key={t.id}>
                      {canComplete ? (
                        <TransferCard
                          {...baseProps}
                          onToggle={() => setPendingTransfer({ id: t.id, toComplete: !completed })}
                        />
                      ) : (
                        <TransferCard {...baseProps} />
                      )}
                      {isPayer && !completed && payoutAccountsQ.isLoading ? (
                        <p className="mt-1.5 px-1 text-small text-ink-faint">
                          계좌 정보를 불러오는 중이에요.
                        </p>
                      ) : null}
                      {isPayer && !completed && payoutAccountsQ.isError ? (
                        <p className="mt-1.5 px-1 text-small text-error">
                          계좌 정보를 불러오지 못했어요. 잠시 후 다시 시도해주세요.
                        </p>
                      ) : null}
                      {isPayer && !completed && payoutAccountsQ.isSuccess && !payeeAccount ? (
                        <p className="mt-1.5 px-1 text-small text-ink-faint">
                          {nicknameOf(t.toUserId)} 님이 아직 정산 계좌를 등록하지 않았어요.
                        </p>
                      ) : null}
                      {isPayer && !completed && payeeAccount && (
                        <Button
                          variant="secondary"
                          className="mt-2 w-full"
                          onClick={() => copyAccount(payeeAccount, t.amount)}
                        >
                          계좌·금액 복사 {payeeAccount.bankName} {payeeAccount.accountNumber}
                        </Button>
                      )}
                      {KAKAOPAY_DEV_ENABLED && isPayer && !completed && (
                        <Button
                          variant="secondary"
                          className="mt-2 w-full"
                          disabled={kakaoPayMut.isPending}
                          onClick={() => kakaoPayMut.mutate(t.id)}
                        >
                          {kakaoPayMut.isPending ? '결제창 여는 중…' : '카카오페이로 보내기'}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {completedTransfers.length > 0 ? (
              <div>
                <p className="mb-2 text-small font-medium text-ink-muted">완료 {completedTransfers.length}</p>
                <ul className="space-y-2" aria-label="완료 송금 내역">
                  {completedTransfers.map((t) => {
                    const canComplete =
                      t.fromUserId === user?.id || t.toUserId === user?.id || isHost;
                    const baseProps = {
                      fromName: nicknameOf(t.fromUserId),
                      toName: nicknameOf(t.toUserId),
                      amount: t.amount,
                      completed: true,
                    };
                    return (
                      <li key={t.id}>
                        {canComplete ? (
                          <TransferCard
                            {...baseProps}
                            onToggle={() => setPendingTransfer({ id: t.id, toComplete: false })}
                          />
                        ) : (
                          <TransferCard {...baseProps} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </section>


      {isHost && s.status !== 'COMPLETED' ? (
        <div className="space-y-2">
          <Button
            variant="brand"
            onClick={() => setConfirmingClose(true)}
            disabled={!canCloseSettlement || !allDone || closeMut.isPending}
            className="w-full"
          >
            {!canCloseSettlement
              ? '정산 시작 후 마감할 수 있어요'
              : allDone && totalTransfers === 0
                ? '정산 마감 (송금 없음)'
                : allDone
                  ? '정산 마감하기'
                  : '모든 송금 완료 후 마감할 수 있어요'}
          </Button>
          {!allDone && pendingTransfers.length > 0 ? (
            <p className="text-center text-small font-medium text-ink-muted">
              남은 송금 {pendingTransfers.length}건을 완료하면 마감할 수 있어요.
            </p>
          ) : null}
          {canCloseSettlement ? null : (
            <p className="text-center text-small font-medium text-ink-muted">
              먼저 모임에서 정산을 시작해야 마감할 수 있어요.
            </p>
          )}
        </div>
      ) : null}

      <Dialog
        open={confirmingClose}
        title="정산을 마감할까요?"
        description="마감 후에는 이 모임의 정산 내역을 변경할 수 없어요."
        confirmLabel="마감"
        confirmTone="danger"
        onConfirm={() => closeMut.mutate()}
        confirmDisabled={closeMut.isPending}
        onCancel={() => setConfirmingClose(false)}
      />

      <Dialog
        open={pendingTransfer !== null}
        title={pendingTransfer?.toComplete ? '송금 완료로 표시할까요?' : '완료를 취소할까요?'}
        description={
          pendingTransfer?.toComplete
            ? '송금을 직접 확인 후 표시해주세요.'
            : '완료 상태를 취소하면 다시 대기 중으로 돌아가요.'
        }
        confirmLabel={pendingTransfer?.toComplete ? '완료로 표시' : '취소'}
        confirmTone={pendingTransfer?.toComplete ? 'primary' : 'danger'}
        onConfirm={() => {
          if (pendingTransfer) {
            completeMut.mutate({ transferId: pendingTransfer.id, completed: pendingTransfer.toComplete });
          }
          setPendingTransfer(null);
        }}
        confirmDisabled={completeMut.isPending}
        onCancel={() => setPendingTransfer(null)}
      />
    </section>
  );
}
